import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, RefreshCw, Building2, FileText, AlertTriangle, Shield,
  CheckCircle2, Clock, Eye, ChevronRight, AlertCircle, TrendingUp,
  ClipboardList, Users, BarChart3, Activity, User, PieChartIcon, Download
} from 'lucide-react';
// Removed recharts imports - using stacked progress bars instead
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const CHART_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const SEVERITY_COLORS_CHART = {
  'Minor': '#22c55e',
  'Moderate': '#f59e0b', 
  'Serious': '#f97316',
  'Severe': '#ef4444',
  'Fatal': '#7f1d1d'
};

const SEVERITY_COLORS = {
  'Minor': 'bg-green-100 text-green-800',
  'Moderate': 'bg-yellow-100 text-yellow-800',
  'Serious': 'bg-orange-100 text-orange-800',
  'Severe': 'bg-red-100 text-red-800',
  'Fatal': 'bg-red-200 text-red-900'
};

const RISK_COLORS = {
  'Low': 'bg-green-100 text-green-800',
  'Medium': 'bg-yellow-100 text-yellow-800',
  'High': 'bg-orange-100 text-orange-800',
  'Critical': 'bg-red-100 text-red-800'
};

// Injury type colors for body map
const INJURY_COLORS = {
  firstAid: '#22c55e',    // Green
  'First Aid': '#22c55e',
  medical: '#f59e0b',     // Yellow/Amber
  'Medical Treatment': '#f59e0b',
  recordable: '#ef4444',  // Red
  'OSHA Recordable': '#ef4444',
};

const GCProjectDetail = ({ user }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState([]);
  const [jsas, setJsas] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [audits, setAudits] = useState([]);
  const [nearMisses, setNearMisses] = useState([]);
  const [bodyMapData, setBodyMapData] = useState({ injuries: [], summary: {} });
  const [bodyMapInjuries, setBodyMapInjuries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [companyAnalytics, setCompanyAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');
  const [acknowledging, setAcknowledging] = useState(null);
  const [selectedInjury, setSelectedInjury] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [incidentDetailLoading, setIncidentDetailLoading] = useState(false);
  // Invite subcontractor state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [availableSubs, setAvailableSubs] = useState([]);
  const [selectedSubToInvite, setSelectedSubToInvite] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchAllData();
    }
  }, [projectId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [projectRes, complianceRes, jsasRes, incidentsRes, auditsRes, nearMissesRes, bodyMapRes, analyticsRes] = await Promise.all([
        axiosInstance.get(`/gc/projects/${projectId}`),
        axiosInstance.get(`/gc/projects/${projectId}/compliance-summary`),
        axiosInstance.get(`/gc/projects/${projectId}/jsas`),
        axiosInstance.get(`/gc/projects/${projectId}/incidents`),
        axiosInstance.get(`/gc/projects/${projectId}/audits`),
        axiosInstance.get(`/gc/projects/${projectId}/near-misses`),
        axiosInstance.get(`/gc/projects/${projectId}/body-map`),
        axiosInstance.get(`/gc/projects/${projectId}/analytics`)
      ]);
      
      setProject(projectRes.data);
      setComplianceSummary(complianceRes.data);
      setJsas(jsasRes.data);
      setIncidents(incidentsRes.data);
      setAudits(auditsRes.data);
      setNearMisses(nearMissesRes.data);
      setBodyMapData(bodyMapRes.data);
      setAnalytics(analyticsRes.data);
      // Transform injuries for draggable body map
      const transformedInjuries = (bodyMapRes.data.injuries || []).map(injury => {
        // Get x/y from various possible locations
        const xPos = injury.x || injury.position?.x || '50%';
        const yPos = injury.y || injury.position?.y || '50%';
        
        // Classify injury type based on description/type for proper color
        let injuryType = injury.type || injury.classification || '';
        const description = (injury.description || injury.injuryType || '').toLowerCase();
        
        // Map serious injuries to correct classification based on description
        if (!injuryType || injuryType === 'unknown' || injuryType === '' || injuryType === 'injury') {
          // OSHA Recordable (severe injuries)
          if (description.includes('concussion') || description.includes('fracture') || 
              description.includes('hospitalization') || description.includes('amputation') ||
              description.includes('unconscious') || description.includes('surgery')) {
            injuryType = 'recordable';
          } 
          // Medical Treatment (requires professional care)
          else if (description.includes('laceration') || description.includes('deep cut') ||
                     description.includes('burn') || description.includes('sprain') || 
                     description.includes('strain') || description.includes('dislocation') ||
                     description.includes('stitches') || description.includes('medical')) {
            injuryType = 'medical';
          } 
          // First Aid (minor injuries)
          else {
            injuryType = 'firstAid';
          }
        }
        
        // Also check the raw type field for known values
        const rawType = (injury.type || '').toLowerCase();
        if (rawType === 'laceration' || rawType.includes('cut')) {
          injuryType = 'medical';
        }
        
        return {
          ...injury,
          x: xPos,
          y: yPos,
          type: injuryType
        };
      });
      setBodyMapInjuries(transformedInjuries);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available subcontractors for invitation
  const fetchAvailableSubs = async () => {
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/available-subcontractors`);
      setAvailableSubs(response.data);
    } catch (error) {
      console.error('Error fetching available subcontractors:', error);
      toast.error('Failed to load available subcontractors');
    }
  };

  // Handle invite subcontractor
  const handleInviteSubcontractor = async () => {
    if (!selectedSubToInvite) {
      toast.error('Please select a subcontractor to invite');
      return;
    }
    setInviting(true);
    try {
      await axiosInstance.post(`/gc/projects/${projectId}/invite`, {
        subCompanyId: selectedSubToInvite
      });
      toast.success('Subcontractor invited successfully');
      setShowInviteDialog(false);
      setSelectedSubToInvite('');
      fetchAllData(); // Refresh project data
    } catch (error) {
      console.error('Error inviting subcontractor:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite subcontractor');
    } finally {
      setInviting(false);
    }
  };

  // Open invite dialog and fetch available subs
  const openInviteDialog = () => {
    fetchAvailableSubs();
    setShowInviteDialog(true);
  };

  // Fetch company-specific analytics
  const fetchCompanyAnalytics = async (companyId) => {
    if (companyId === 'all') {
      setCompanyAnalytics(null);
      return;
    }
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/analytics/by-company/${companyId}`);
      setCompanyAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching company analytics:', error);
      toast.error('Failed to load company analytics');
    }
  };

  // Handle company selection change
  useEffect(() => {
    if (selectedCompany && selectedCompany !== 'all') {
      fetchCompanyAnalytics(selectedCompany);
    } else {
      setCompanyAnalytics(null);
    }
  }, [selectedCompany]);

  // Handle drag start
  const handleDragStart = (e, injury) => {
    e.preventDefault();
    setDraggingId(injury.id);
    setSelectedInjury(injury);
    
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!draggingId) return;
    e.preventDefault();
    
    const container = document.getElementById('gc-body-map-container');
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    
    // Clamp values between 0 and 100
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    setBodyMapInjuries(prev => prev.map(injury => 
      injury.id === draggingId 
        ? { ...injury, x: `${clampedX}%`, y: `${clampedY}%` }
        : injury
    ));
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggingId(null);
  };

  // Add non-passive touch event listeners to prevent scrolling while dragging
  useEffect(() => {
    const container = document.getElementById('gc-body-map-container');
    if (!container) return;
    
    const preventTouchMove = (e) => {
      if (draggingId) {
        e.preventDefault();
      }
    };
    
    container.addEventListener('touchmove', preventTouchMove, { passive: false });
    
    return () => {
      container.removeEventListener('touchmove', preventTouchMove);
    };
  }, [draggingId]);

  const handleAcknowledgeJSA = async (jsaId) => {
    setAcknowledging(jsaId);
    try {
      await axiosInstance.post(`/gc/projects/${projectId}/jsas/${jsaId}/acknowledge`);
      toast.success('JSA acknowledged');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge JSA');
    } finally {
      setAcknowledging(null);
    }
  };

  const handleAcknowledgeIncident = async (incidentId, requestCorrectiveAction = false) => {
    setAcknowledging(incidentId);
    try {
      await axiosInstance.post(`/gc/projects/${projectId}/incidents/${incidentId}/acknowledge?request_corrective_action=${requestCorrectiveAction}`);
      toast.success(requestCorrectiveAction ? 'Incident acknowledged - corrective action requested' : 'Incident acknowledged');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge incident');
    } finally {
      setAcknowledging(null);
    }
  };

  // View incident details (redacted for GC)
  const handleViewIncident = async (incidentId) => {
    setIncidentDetailLoading(true);
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/incidents/${incidentId}`);
      setSelectedIncident(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load incident details');
    } finally {
      setIncidentDetailLoading(false);
    }
  };

  // Download GC Incident Summary PDF (only for closed incidents)
  const handleDownloadIncidentPDF = async (incidentId) => {
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/incidents/${incidentId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GC_Incident_Summary_${incidentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('GC Incident Summary PDF downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PDF. Only closed incidents have summaries.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate overview stats
  const stats = {
    totalSubs: complianceSummary.length,
    totalJsas: jsas.length,
    totalIncidents: incidents.length,
    totalAudits: audits.length,
    totalNearMisses: nearMisses.length,
    avgAuditScore: audits.length > 0 
      ? Math.round(audits.filter(a => a.safetyScore).reduce((sum, a) => sum + a.safetyScore, 0) / audits.filter(a => a.safetyScore).length)
      : null,
    pendingActions: complianceSummary.reduce((sum, s) => sum + (s.pendingCorrectiveActions || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/gc/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{project?.name || 'Project'}</h1>
              <p className="text-gray-600">{project?.location} • {stats.totalSubs} Subcontractors</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={openInviteDialog}>
              <Users className="w-4 h-4 mr-2" />
              Invite Subcontractor
            </Button>
            <Button variant="outline" onClick={fetchAllData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.totalSubs}</p>
              <p className="text-xs text-gray-500">Subcontractors</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.totalJsas}</p>
              <p className="text-xs text-gray-500">JSAs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 text-orange-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.totalIncidents}</p>
              <p className="text-xs text-gray-500">Incidents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Shield className="w-6 h-6 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.totalAudits}</p>
              <p className="text-xs text-gray-500">Audits</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.avgAuditScore || 'N/A'}</p>
              <p className="text-xs text-gray-500">Avg Audit Score</p>
            </CardContent>
          </Card>
          <Card className={stats.pendingActions > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.pendingActions}</p>
              <p className="text-xs text-gray-500">Pending Actions</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="analytics">📊 Analytics</TabsTrigger>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="body-map">Body Map ({bodyMapData.summary?.total || 0})</TabsTrigger>
            <TabsTrigger value="jsas">JSAs ({jsas.length})</TabsTrigger>
            <TabsTrigger value="incidents">Incidents ({incidents.length})</TabsTrigger>
            <TabsTrigger value="audits">Audits ({audits.length})</TabsTrigger>
            <TabsTrigger value="near-misses">Near Misses ({nearMisses.length})</TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {!analytics ? (
              <Card>
                <CardContent className="text-center py-12">
                  <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No analytics data available</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Company Filter */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">View Analytics By</h3>
                        <p className="text-sm text-gray-500">Select a subcontractor or view project totals</p>
                      </div>
                      <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Select company..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">📊 All Subcontractors (Project Total)</SelectItem>
                          {complianceSummary.map(sub => (
                            <SelectItem key={sub.subCompanyId} value={sub.subCompanyId}>
                              🏢 {sub.subCompanyName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <AlertTriangle className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold">{(companyAnalytics || analytics)?.summary?.totalIncidents || 0}</p>
                      <p className="text-xs text-gray-500">Incidents</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <FileText className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold">{(companyAnalytics || analytics)?.summary?.totalJsas || 0}</p>
                      <p className="text-xs text-gray-500">JSAs</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Shield className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold">{(companyAnalytics || analytics)?.summary?.totalAudits || 0}</p>
                      <p className="text-xs text-gray-500">Audits</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <AlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold">{(companyAnalytics || analytics)?.summary?.totalHazardsIdentified || 0}</p>
                      <p className="text-xs text-gray-500">Hazards</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold">{(companyAnalytics || analytics)?.summary?.averageAuditScore || 'N/A'}</p>
                      <p className="text-xs text-gray-500">Avg Audit Score</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <User className="w-6 h-6 text-red-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold">{(companyAnalytics || analytics)?.summary?.totalInjuries || 0}</p>
                      <p className="text-xs text-gray-500">Injuries</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row 1 - Stacked Progress Bars */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Incidents by Type */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Incidents by Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const data = (companyAnalytics?.incidents?.byType) || analytics?.charts?.incidentsByType || [];
                        const total = data.reduce((sum, item) => sum + item.value, 0);
                        return data.length > 0 ? (
                          <div className="space-y-3">
                            {data.map((item, idx) => {
                              const percentage = total > 0 ? (item.value / total) * 100 : 0;
                              return (
                                <div key={item.name} className="flex items-center gap-3">
                                  <span className="text-xs text-gray-600 w-24 truncate" title={item.name}>{item.name}</span>
                                  <div className="flex-1 bg-gray-100 rounded-full h-4">
                                    <div 
                                      className="h-4 rounded-full transition-all duration-500" 
                                      style={{
                                        width: `${Math.max(percentage, 5)}%`,
                                        backgroundColor: CHART_COLORS[idx % CHART_COLORS.length]
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic py-4 text-center">No incidents recorded</p>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Incidents by Severity */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Incidents by Severity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const data = (companyAnalytics?.incidents?.bySeverity) || analytics?.charts?.incidentsBySeverity || [];
                        const total = data.reduce((sum, item) => sum + item.value, 0);
                        const severityColors = {
                          'Minor': '#22c55e', 'Low': '#22c55e',
                          'Moderate': '#f59e0b', 'Medium': '#f59e0b',
                          'Serious': '#f97316', 'High': '#f97316',
                          'Severe': '#ef4444', 'Critical': '#ef4444',
                          'Fatal': '#7f1d1d'
                        };
                        return data.length > 0 ? (
                          <div className="space-y-3">
                            {data.map((item) => {
                              const percentage = total > 0 ? (item.value / total) * 100 : 0;
                              return (
                                <div key={item.name} className="flex items-center gap-3">
                                  <span className="text-xs text-gray-600 w-16 capitalize">{item.name}</span>
                                  <div className="flex-1 bg-gray-100 rounded-full h-4">
                                    <div 
                                      className="h-4 rounded-full transition-all duration-500" 
                                      style={{
                                        width: `${Math.max(percentage, 5)}%`,
                                        backgroundColor: severityColors[item.name] || '#6b7280'
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic py-4 text-center">No incidents recorded</p>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row 2 - Only show for project-wide view */}
                {selectedCompany === 'all' && analytics?.charts?.incidentsByCompany?.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Incidents by Company */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Incidents by Subcontractor</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const data = analytics.charts.incidentsByCompany || [];
                          const maxValue = Math.max(...data.map(d => d.value), 1);
                          return data.length > 0 ? (
                            <div className="space-y-3">
                              {data.map((item, idx) => {
                                const percentage = (item.value / maxValue) * 100;
                                return (
                                  <div key={item.name} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-32 truncate" title={item.name}>{item.name}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500 bg-red-500" 
                                        style={{ width: `${Math.max(percentage, 5)}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic py-4 text-center">No data available</p>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    {/* JSAs by Company */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">JSAs by Subcontractor</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const data = analytics.charts.jsasByCompany || [];
                          const maxValue = Math.max(...data.map(d => d.value), 1);
                          return data.length > 0 ? (
                            <div className="space-y-3">
                              {data.map((item, idx) => {
                                const percentage = (item.value / maxValue) * 100;
                                return (
                                  <div key={item.name} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-32 truncate" title={item.name}>{item.name}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500 bg-blue-500" 
                                        style={{ width: `${Math.max(percentage, 5)}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic py-4 text-center">No data available</p>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Charts Row 3 - Near Misses & Injuries */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Near Misses by Category */}
                  {analytics?.charts?.nearMissByCategory?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Near Misses by Category</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const data = analytics.charts.nearMissByCategory || [];
                          const total = data.reduce((sum, item) => sum + item.value, 0);
                          return (
                            <div className="space-y-3">
                              {data.map((item, idx) => {
                                const percentage = total > 0 ? (item.value / total) * 100 : 0;
                                return (
                                  <div key={item.name} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-28 truncate" title={item.name}>{item.name}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: CHART_COLORS[idx % CHART_COLORS.length]
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  )}

                  {/* Injuries by Body Part */}
                  {analytics?.charts?.bodyMapByPart?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Injuries by Body Part</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const data = analytics.charts.bodyMapByPart || [];
                          const total = data.reduce((sum, item) => sum + item.value, 0);
                          return (
                            <div className="space-y-3">
                              {data.map((item, idx) => {
                                const percentage = total > 0 ? (item.value / total) * 100 : 0;
                                return (
                                  <div key={item.name} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-24 truncate capitalize" title={item.name}>{item.name}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: CHART_COLORS[idx % CHART_COLORS.length]
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Charts Row 4 - Injury Classification & Audit Scores */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Injuries by Classification */}
                  {analytics?.charts?.bodyMapByType?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Injuries by Classification</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const data = analytics.charts.bodyMapByType || [];
                          const total = data.reduce((sum, item) => sum + item.value, 0);
                          const classificationColors = {
                            'OSHA Recordable': '#ef4444', 'recordable': '#ef4444',
                            'Medical Treatment': '#f59e0b', 'medical': '#f59e0b',
                            'First Aid': '#22c55e', 'firstAid': '#22c55e'
                          };
                          return (
                            <div className="space-y-3">
                              {data.map((item, idx) => {
                                const percentage = total > 0 ? (item.value / total) * 100 : 0;
                                return (
                                  <div key={item.name} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-28 truncate" title={item.name}>{item.name}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${Math.max(percentage, 5)}%`,
                                          backgroundColor: classificationColors[item.name] || CHART_COLORS[idx % CHART_COLORS.length]
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-10 text-right">{item.value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  )}

                  {/* Audit Scores by Company (project view only) */}
                  {selectedCompany === 'all' && analytics?.charts?.auditScoresByCompany?.filter(a => a.score > 0)?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Audit Scores by Subcontractor</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const data = analytics.charts.auditScoresByCompany.filter(a => a.score > 0) || [];
                          return (
                            <div className="space-y-3">
                              {data.map((item) => {
                                const scoreColor = item.score >= 80 ? '#22c55e' : item.score >= 60 ? '#f59e0b' : '#ef4444';
                                return (
                                  <div key={item.name} className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600 w-32 truncate" title={item.name}>{item.name}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                                      <div 
                                        className="h-4 rounded-full transition-all duration-500" 
                                        style={{
                                          width: `${item.score}%`,
                                          backgroundColor: scoreColor
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold w-12 text-right">{item.score}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>Subcontractor Compliance Summary</CardTitle>
                <CardDescription>Weekly compliance overview for all linked subcontractors</CardDescription>
              </CardHeader>
              <CardContent>
                {complianceSummary.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No subcontractors linked to this project yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Subcontractor</TableHead>
                        <TableHead className="text-center">JSAs (7 days)</TableHead>
                        <TableHead className="text-center">Incidents (30 days)</TableHead>
                        <TableHead className="text-center">Latest Audit</TableHead>
                        <TableHead className="text-center">Risk Level</TableHead>
                        <TableHead className="text-center">Pending Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {complianceSummary.map((sub) => (
                        <TableRow key={sub.subCompanyId}>
                          <TableCell className="font-medium">{sub.subCompanyName}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={sub.jsasThisWeek > 0 ? 'default' : 'secondary'}>
                              {sub.jsasThisWeek}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={sub.incidentsThisMonth > 0 ? 'destructive' : 'secondary'}>
                              {sub.incidentsThisMonth}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {sub.latestAuditScore ? (
                              <span className="font-medium">{sub.latestAuditScore}/100</span>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {sub.latestAuditRisk ? (
                              <Badge className={RISK_COLORS[sub.latestAuditRisk] || 'bg-gray-100'}>
                                {sub.latestAuditRisk}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={sub.pendingCorrectiveActions > 0 ? 'destructive' : 'secondary'}>
                              {sub.pendingCorrectiveActions}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Body Map Tab */}
          <TabsContent value="body-map">
            <Card>
              <CardHeader>
                <CardTitle>Injury Body Map</CardTitle>
                <CardDescription>Visual representation of injuries reported by subcontractors</CardDescription>
              </CardHeader>
              <CardContent>
                {bodyMapInjuries.length === 0 ? (
                  <div className="text-center py-8">
                    <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No injury data available</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Body Silhouette - Draggable */}
                    <div className="lg:col-span-2">
                      <div 
                        id="gc-body-map-container"
                        className="relative w-full max-w-[500px] h-[450px] mx-auto bg-white rounded-lg select-none"
                        onMouseMove={handleDragMove}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                        onTouchMove={handleDragMove}
                        onTouchEnd={handleDragEnd}
                      >
                        {/* Professional silhouette image - same as main analytics */}
                        <img 
                          src="https://customer-assets.emergentagent.com/job_formbuilder-7/artifacts/dj7vgm3e_injury%20silouette.png" 
                          alt="Human body silhouette"
                          className="w-full h-full object-contain pointer-events-none"
                          style={{ 
                            filter: 'contrast(1.1)',
                            mixBlendMode: 'multiply'
                          }}
                          draggable={false}
                        />
                        
                        {/* Draggable Injury Dots */}
                        {bodyMapInjuries.map((injury) => {
                          const injuryType = injury.type || injury.classification || 'firstAid';
                          const color = INJURY_COLORS[injuryType] || INJURY_COLORS.firstAid;
                          return (
                            <div
                              key={injury.id}
                              className={`absolute cursor-grab transform -translate-x-1/2 -translate-y-1/2 transition-transform z-10 ${
                                draggingId === injury.id ? 'cursor-grabbing scale-150' : 'hover:scale-125'
                              }`}
                              style={{
                                left: injury.x,
                                top: injury.y,
                              }}
                              onMouseDown={(e) => handleDragStart(e, injury)}
                              onTouchStart={(e) => handleDragStart(e, injury)}
                              onClick={() => !draggingId && setSelectedInjury(injury)}
                              title={`${injury.bodyPart}: ${injury.description} (Drag to reposition)`}
                            >
                              <div 
                                className={`w-5 h-5 rounded-full border-2 border-white shadow-lg ${
                                  draggingId === injury.id ? 'ring-2 ring-blue-400 ring-offset-2' : ''
                                }`}
                                style={{ 
                                  backgroundColor: color,
                                  boxShadow: `0 0 10px ${color}`,
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Legend */}
                      <div className="flex justify-center gap-6 mt-4">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-green-500"></div>
                          <span className="text-sm">First Aid</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                          <span className="text-sm">Medical Treatment</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-red-500"></div>
                          <span className="text-sm">OSHA Recordable</span>
                        </div>
                      </div>
                      
                      {/* Drag hint */}
                      <p className="text-center text-xs text-gray-400 mt-2">
                        💡 Drag injury dots to reposition them on the body map
                      </p>
                    </div>
                    
                    {/* Injury List */}
                    <div>
                      <h4 className="font-medium mb-3">Injuries ({bodyMapInjuries.length})</h4>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {bodyMapInjuries.map((injury) => {
                          const injuryType = injury.type || injury.classification || 'firstAid';
                          const color = INJURY_COLORS[injuryType] || INJURY_COLORS.firstAid;
                          return (
                            <div 
                              key={injury.id}
                              className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${selectedInjury?.id === injury.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                              onClick={() => setSelectedInjury(injury)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                                <span className="font-medium text-sm capitalize">{injury.bodyPart?.replace(/([A-Z])/g, ' $1').trim()}</span>
                              </div>
                              <p className="text-xs text-gray-600 line-clamp-2">{injury.description}</p>
                              <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                                <span>{injury.subCompanyName}</span>
                                <span>{injury.date ? new Date(injury.date).toLocaleDateString() : 'N/A'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Summary Stats */}
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-sm mb-2">Summary by Type</h5>
                        <div className="space-y-1">
                          {Object.entries(bodyMapData.summary?.byType || {}).map(([type, count]) => (
                            <div key={type} className="flex items-center justify-between text-sm">
                              <span className="capitalize">{type.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <Badge variant="secondary">{count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* JSAs Tab */}
          <TabsContent value="jsas">
            <Card>
              <CardHeader>
                <CardTitle>Job Safety Analyses</CardTitle>
                <CardDescription>Recent JSAs from linked subcontractors</CardDescription>
              </CardHeader>
              <CardContent>
                {jsas.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No JSAs found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Subcontractor</TableHead>
                        <TableHead>Work Area</TableHead>
                        <TableHead>Hazards</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jsas.map((jsa) => (
                        <TableRow key={jsa.id}>
                          <TableCell className="font-medium max-w-xs truncate">{jsa.taskDescription}</TableCell>
                          <TableCell>{jsa.subCompanyName}</TableCell>
                          <TableCell>{jsa.workArea || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{jsa.hazardCount || 0} hazards</Badge>
                          </TableCell>
                          <TableCell>{formatDate(jsa.createdAt)}</TableCell>
                          <TableCell>
                            <Badge variant={jsa.status === 'signed' ? 'default' : 'secondary'}>
                              {jsa.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {jsa.acknowledgedByGC ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Acknowledged
                              </Badge>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleAcknowledgeJSA(jsa.id)}
                                disabled={acknowledging === jsa.id}
                              >
                                {acknowledging === jsa.id ? 'Processing...' : 'Acknowledge'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents">
            <Card>
              <CardHeader>
                <CardTitle>Incidents</CardTitle>
                <CardDescription>Closed incident summaries from linked subcontractors. Pending incidents show status only.</CardDescription>
              </CardHeader>
              <CardContent>
                {incidents.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No incidents reported</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Subcontractor</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incidents.map((inc) => (
                        <TableRow key={inc.id} className={!inc.gcSummaryAvailable ? 'opacity-60' : ''}>
                          <TableCell className="font-medium">{inc.incidentType || '—'}</TableCell>
                          <TableCell>{inc.subCompanyName}</TableCell>
                          <TableCell>
                            {inc.gcSummaryAvailable && inc.severity ? (
                              <Badge className={SEVERITY_COLORS[inc.severity] || 'bg-gray-100'}>
                                {inc.severity}
                              </Badge>
                            ) : (
                              <span className="text-gray-400 text-sm italic">Pending</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {inc.gcSummaryAvailable ? (inc.location || 'N/A') : (
                              <span className="text-gray-400 text-sm italic">—</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(inc.incidentDate || inc.createdAt)}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={inc.status === 'closed' || inc.status === 'resolved' ? 'secondary' : 'outline'}
                              className={inc.status === 'closed' || inc.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                            >
                              {inc.status === 'closed' || inc.status === 'resolved' ? 'Closed' : 'Under Review'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {!inc.gcSummaryAvailable ? (
                              <span className="text-xs text-gray-400">Available on closure</span>
                            ) : (
                              <div className="flex gap-1 items-center">
                                {inc.acknowledgedByGC ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Acknowledged
                                  </Badge>
                                ) : (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleAcknowledgeIncident(inc.id, false)}
                                      disabled={acknowledging === inc.id}
                                    >
                                      Ack
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      onClick={() => handleAcknowledgeIncident(inc.id, true)}
                                      disabled={acknowledging === inc.id}
                                    >
                                      + Action
                                    </Button>
                                  </>
                                )}
                                {/* View Details Button */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewIncident(inc.id)}
                                  title="View Incident Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {/* PDF Download for closed incidents */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDownloadIncidentPDF(inc.id)}
                                  title="Download GC Incident Summary PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audits Tab */}
          <TabsContent value="audits">
            <Card>
              <CardHeader>
                <CardTitle>Safety Audits</CardTitle>
                <CardDescription>Audit results from linked subcontractors</CardDescription>
              </CardHeader>
              <CardContent>
                {audits.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No audits found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Subcontractor</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Risk Level</TableHead>
                        <TableHead>Pass Rate</TableHead>
                        <TableHead>Corrective Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {audits.map((audit) => (
                        <TableRow key={audit.id}>
                          <TableCell className="font-medium">{audit.subCompanyName}</TableCell>
                          <TableCell>{audit.location || 'N/A'}</TableCell>
                          <TableCell>{formatDate(audit.auditDate || audit.createdAt)}</TableCell>
                          <TableCell>
                            <span className={`font-bold ${
                              audit.safetyScore >= 80 ? 'text-green-600' :
                              audit.safetyScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {audit.safetyScore || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {audit.riskLevel ? (
                              <Badge className={RISK_COLORS[audit.riskLevel] || 'bg-gray-100'}>
                                {audit.riskLevel}
                              </Badge>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {audit.checklistSummary ? (
                              <span className={`font-medium ${
                                audit.checklistSummary.passRate >= 80 ? 'text-green-600' :
                                audit.checklistSummary.passRate >= 60 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {audit.checklistSummary.passRate}%
                              </span>
                            ) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={audit.correctiveActionCount > 0 ? 'destructive' : 'secondary'}>
                              {audit.correctiveActionCount || 0}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Near Misses Tab */}
          <TabsContent value="near-misses">
            <Card>
              <CardHeader>
                <CardTitle>Near Miss Reports</CardTitle>
                <CardDescription>Near miss incidents from linked subcontractors</CardDescription>
              </CardHeader>
              <CardContent>
                {nearMisses.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No near misses reported</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Subcontractor</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Potential Severity</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {nearMisses.map((nm) => (
                        <TableRow key={nm.id}>
                          <TableCell className="font-medium">{nm.category}</TableCell>
                          <TableCell>{nm.subCompanyName}</TableCell>
                          <TableCell>{nm.location || 'N/A'}</TableCell>
                          <TableCell className="max-w-xs truncate">{nm.description}</TableCell>
                          <TableCell>
                            {nm.potentialSeverity && (
                              <Badge className={SEVERITY_COLORS[nm.potentialSeverity] || 'bg-gray-100'}>
                                {nm.potentialSeverity}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(nm.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* GC Oversight Disclaimer Footer - REQUIRED per spec */}
        <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-500 text-center">
            <strong>GC Oversight Notice:</strong> This summary is provided for GC oversight purposes only. 
            All investigation details, corrective actions, and safety program management remain the responsibility 
            of the subcontractor. Data shown excludes worker personal information and medical details per privacy requirements.
          </p>
        </div>
      </div>

      {/* Incident Detail Modal (Redacted View for GC) */}
      <Dialog open={!!selectedIncident} onOpenChange={() => setSelectedIncident(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Incident Report (GC Summary)
            </DialogTitle>
            <DialogDescription>
              {selectedIncident?.subCompanyName} - {selectedIncident?.divisionName}
            </DialogDescription>
          </DialogHeader>

          {incidentDetailLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : selectedIncident?.gcSummaryAvailable ? (
            <div className="space-y-6">
              {/* Incident Overview */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Incident Type</p>
                  <p className="font-medium">{selectedIncident.incidentType || 'N/A'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Severity</p>
                  <Badge className={SEVERITY_COLORS[selectedIncident.severity] || 'bg-gray-100'}>
                    {selectedIncident.severity || 'N/A'}
                  </Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(selectedIncident.incidentDate)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Time</p>
                  <p className="font-medium">{selectedIncident.incidentTime || 'N/A'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium">{selectedIncident.location || 'N/A'}</p>
                </div>
              </div>

              {/* Description (Sanitized) */}
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs text-blue-600 mb-2 font-medium">Description (Redacted)</p>
                <p className="text-gray-700">{selectedIncident.description || 'No description available'}</p>
              </div>

              {/* People Involved (Counts Only) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">People Involved</p>
                  <p className="text-xl font-bold">{selectedIncident.peopleInvolvedCount || 0}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Witnesses</p>
                  <p className="text-xl font-bold">{selectedIncident.witnessCount || 0}</p>
                </div>
              </div>

              {/* Injured Parties (No Names) */}
              {selectedIncident.injuredPartiesCount > 0 && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-xs text-red-600 mb-2 font-medium">Injuries ({selectedIncident.injuredPartiesCount})</p>
                  <div className="space-y-3">
                    {selectedIncident.injuredParties?.map((ip, idx) => (
                      <div key={idx} className="p-3 bg-white rounded-lg">
                        <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                          <div>
                            <span className="text-gray-500">Injury Type:</span>
                            <p className="font-semibold">{ip.injuryType || 'Not specified'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Body Part:</span>
                            <p className="font-semibold">{ip.bodyPart || 'Not specified'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Treatment:</span>
                            <p className="font-semibold">{ip.treatmentRequired || 'Not specified'}</p>
                          </div>
                        </div>
                        {ip.injuryDescription && (
                          <div className="text-sm mt-2 pt-2 border-t border-red-100">
                            <span className="text-gray-500">Description:</span>
                            <p className="text-gray-700">{ip.injuryDescription}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Corrective Actions (Sanitized) */}
              {selectedIncident.correctiveActions && (
                <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                  <p className="text-xs text-green-600 mb-2 font-medium">Corrective Actions</p>
                  <p className="text-gray-700">{selectedIncident.correctiveActions}</p>
                </div>
              )}

              {/* Preventive Measures (Sanitized) */}
              {selectedIncident.preventiveMeasures && (
                <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
                  <p className="text-xs text-purple-600 mb-2 font-medium">Preventive Measures</p>
                  <p className="text-gray-700">{selectedIncident.preventiveMeasures}</p>
                </div>
              )}

              {/* AI Analysis */}
              {selectedIncident.aiAnalysis && (
                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <p className="text-xs text-indigo-600 mb-2 font-medium">AI Analysis Summary</p>
                  {selectedIncident.aiAnalysis.rootCauses?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500">Root Causes:</p>
                      <ul className="list-disc list-inside text-sm text-gray-700">
                        {selectedIncident.aiAnalysis.rootCauses.map((cause, idx) => (
                          <li key={idx}>{cause}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedIncident.aiAnalysis.recommendations?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500">Recommendations:</p>
                      <ul className="list-disc list-inside text-sm text-gray-700">
                        {selectedIncident.aiAnalysis.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Privacy Notice */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-700">
                  <strong>Privacy Notice:</strong> Worker names, witness identities, and medical details have been redacted 
                  in accordance with privacy requirements. This is a read-only summary for GC oversight purposes.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <p className="text-gray-700 font-medium">Incident Under Review</p>
              <p className="text-gray-500 text-sm mt-2">
                {selectedIncident?.pendingMessage || 'Full summary will be available once the incident is closed.'}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedIncident(null)}>
              Close
            </Button>
            {selectedIncident?.gcSummaryAvailable && (
              <Button onClick={() => handleDownloadIncidentPDF(selectedIncident.id)}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Subcontractor Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Invite Subcontractor
            </DialogTitle>
            <DialogDescription>
              Select a subcontractor company to invite to this project
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {availableSubs.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No available subcontractors to invite</p>
                <p className="text-sm mt-1">All registered subcontractors have already been invited to this project</p>
              </div>
            ) : (
              <Select value={selectedSubToInvite} onValueChange={setSelectedSubToInvite}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a subcontractor..." />
                </SelectTrigger>
                <SelectContent>
                  {availableSubs.map((sub) => (
                    <SelectItem key={sub.id} value={sub.id}>
                      {sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInviteSubcontractor} 
              disabled={!selectedSubToInvite || inviting}
            >
              {inviting ? 'Inviting...' : 'Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GCProjectDetail;
